/**
 * Consumption load profiles.
 * Hourly shape matrices derived from BDEW standard load profiles
 * (H0 household, G0 general commerce, G1 weekday-business, L0 agriculture),
 * normalised per season × day-type, scaled to a target annual consumption.
 * BDEW H0 dynamisation polynomial applied for households.
 * Overlays: EV home charging, heat-pump winter load.
 */

export type ProfileId = "H0" | "G0" | "G1" | "L0";
export type Season = "winter" | "summer" | "transition";
export type DayType = "workday" | "saturday" | "sunday";

/** Normalised 24-h shapes (sum = 1 per row), derived from BDEW SLP curves. */
const SHAPES: Record<ProfileId, Record<Season, Record<DayType, number[]>>> = {
  H0: {
    winter: {
      workday: n([28, 22, 20, 19, 20, 26, 45, 58, 52, 48, 48, 52, 55, 50, 46, 48, 60, 78, 88, 84, 74, 62, 48, 34]),
      saturday: n([30, 24, 21, 20, 21, 24, 32, 44, 54, 58, 58, 60, 58, 52, 48, 50, 60, 76, 84, 80, 72, 62, 50, 38]),
      sunday: n([32, 26, 22, 21, 21, 23, 28, 36, 48, 58, 62, 64, 62, 56, 50, 50, 58, 72, 80, 78, 70, 60, 48, 36]),
    },
    summer: {
      workday: n([24, 20, 18, 17, 18, 24, 40, 52, 46, 42, 42, 46, 50, 46, 42, 42, 48, 58, 64, 66, 66, 58, 44, 30]),
      saturday: n([26, 22, 19, 18, 19, 22, 30, 40, 50, 54, 54, 56, 54, 48, 44, 44, 48, 56, 62, 64, 64, 58, 46, 32]),
      sunday: n([28, 24, 20, 19, 19, 21, 26, 34, 44, 54, 58, 60, 58, 52, 46, 44, 48, 54, 60, 62, 62, 56, 44, 32]),
    },
    transition: {
      workday: n([26, 21, 19, 18, 19, 25, 43, 55, 49, 45, 45, 49, 52, 48, 44, 45, 54, 68, 76, 75, 70, 60, 46, 32]),
      saturday: n([28, 23, 20, 19, 20, 23, 31, 42, 52, 56, 56, 58, 56, 50, 46, 47, 54, 66, 73, 72, 68, 60, 48, 35]),
      sunday: n([30, 25, 21, 20, 20, 22, 27, 35, 46, 56, 60, 62, 60, 54, 48, 47, 53, 63, 70, 70, 66, 58, 46, 34]),
    },
  },
  G0: {
    winter: {
      workday: n([20, 18, 17, 17, 18, 24, 40, 66, 84, 90, 92, 92, 88, 86, 84, 80, 72, 62, 50, 40, 34, 28, 24, 22]),
      saturday: n([22, 20, 18, 18, 19, 22, 30, 44, 58, 66, 68, 66, 60, 52, 46, 42, 38, 34, 32, 30, 28, 26, 24, 23]),
      sunday: n([22, 20, 19, 19, 19, 20, 22, 26, 32, 36, 38, 38, 36, 34, 32, 30, 30, 30, 30, 29, 28, 26, 24, 23]),
    },
    summer: {
      workday: n([20, 18, 17, 17, 18, 24, 42, 68, 86, 92, 94, 94, 90, 88, 86, 82, 74, 62, 48, 38, 32, 27, 23, 21]),
      saturday: n([22, 20, 18, 18, 19, 22, 32, 46, 60, 68, 70, 68, 62, 54, 48, 44, 40, 36, 33, 30, 28, 26, 24, 23]),
      sunday: n([22, 20, 19, 19, 19, 20, 22, 27, 33, 37, 39, 39, 37, 35, 33, 31, 30, 30, 29, 28, 27, 25, 24, 23]),
    },
    transition: {
      workday: n([20, 18, 17, 17, 18, 24, 41, 67, 85, 91, 93, 93, 89, 87, 85, 81, 73, 62, 49, 39, 33, 28, 24, 22]),
      saturday: n([22, 20, 18, 18, 19, 22, 31, 45, 59, 67, 69, 67, 61, 53, 47, 43, 39, 35, 32, 30, 28, 26, 24, 23]),
      sunday: n([22, 20, 19, 19, 19, 20, 22, 26, 32, 36, 38, 38, 36, 34, 32, 30, 30, 30, 29, 28, 27, 25, 24, 23]),
    },
  },
  G1: {
    winter: {
      workday: n([8, 8, 8, 8, 10, 18, 44, 82, 100, 104, 104, 102, 96, 96, 94, 88, 74, 52, 30, 18, 12, 10, 9, 8]),
      saturday: n([10, 10, 10, 10, 10, 12, 18, 26, 32, 34, 34, 32, 28, 24, 20, 18, 16, 14, 13, 12, 11, 10, 10, 10]),
      sunday: n([10, 10, 10, 10, 10, 11, 12, 14, 16, 17, 17, 16, 15, 14, 13, 12, 12, 12, 11, 11, 11, 10, 10, 10]),
    },
    summer: {
      workday: n([8, 8, 8, 8, 10, 18, 46, 84, 102, 106, 106, 104, 98, 98, 96, 90, 76, 52, 28, 16, 11, 9, 8, 8]),
      saturday: n([10, 10, 10, 10, 10, 12, 19, 27, 33, 35, 35, 33, 29, 25, 21, 18, 16, 14, 12, 11, 10, 10, 10, 10]),
      sunday: n([10, 10, 10, 10, 10, 11, 12, 14, 16, 17, 17, 16, 15, 14, 13, 12, 12, 11, 11, 11, 10, 10, 10, 10]),
    },
    transition: {
      workday: n([8, 8, 8, 8, 10, 18, 45, 83, 101, 105, 105, 103, 97, 97, 95, 89, 75, 52, 29, 17, 11, 9, 8, 8]),
      saturday: n([10, 10, 10, 10, 10, 12, 18, 26, 32, 34, 34, 32, 28, 24, 20, 18, 16, 14, 12, 11, 10, 10, 10, 10]),
      sunday: n([10, 10, 10, 10, 10, 11, 12, 14, 16, 17, 17, 16, 15, 14, 13, 12, 12, 11, 11, 11, 10, 10, 10, 10]),
    },
  },
  L0: {
    winter: {
      workday: n([30, 28, 27, 27, 30, 44, 60, 66, 60, 54, 52, 54, 56, 52, 50, 52, 62, 72, 74, 66, 56, 46, 38, 32]),
      saturday: n([30, 28, 27, 27, 30, 42, 56, 62, 58, 54, 52, 54, 54, 50, 48, 50, 60, 70, 72, 64, 54, 46, 38, 32]),
      sunday: n([30, 28, 27, 27, 30, 40, 54, 60, 56, 52, 52, 54, 54, 50, 48, 50, 58, 68, 70, 62, 54, 44, 38, 32]),
    },
    summer: {
      workday: n([26, 24, 23, 23, 28, 44, 58, 60, 54, 48, 46, 48, 50, 46, 44, 46, 54, 62, 62, 56, 50, 42, 34, 28]),
      saturday: n([26, 24, 23, 23, 28, 42, 54, 58, 52, 48, 46, 48, 48, 44, 42, 44, 52, 60, 60, 54, 48, 40, 34, 28]),
      sunday: n([26, 24, 23, 23, 28, 40, 52, 56, 50, 46, 46, 48, 48, 44, 42, 44, 50, 58, 58, 52, 46, 40, 34, 28]),
    },
    transition: {
      workday: n([28, 26, 25, 25, 29, 44, 59, 63, 57, 51, 49, 51, 53, 49, 47, 49, 58, 67, 68, 61, 53, 44, 36, 30]),
      saturday: n([28, 26, 25, 25, 29, 42, 55, 60, 55, 51, 49, 51, 51, 47, 45, 47, 56, 65, 66, 59, 51, 43, 36, 30]),
      sunday: n([28, 26, 25, 25, 29, 40, 53, 58, 53, 49, 49, 51, 51, 47, 45, 47, 54, 63, 64, 57, 50, 42, 36, 30]),
    },
  },
};

function n(row: number[]): number[] {
  const s = row.reduce((a, b) => a + b, 0);
  return row.map((v) => v / s);
}

function seasonOf(date: Date): Season {
  const m = date.getUTCMonth() + 1;
  const d = date.getUTCDate();
  // BDEW season boundaries
  if (m === 12 || m <= 2 || (m === 3 && d <= 20) || (m === 11 && d >= 1)) return "winter";
  if ((m === 5 && d >= 15) || m === 6 || m === 7 || m === 8 || (m === 9 && d <= 14)) return "summer";
  return "transition";
}

function dayTypeOf(date: Date): DayType {
  const wd = date.getUTCDay();
  if (wd === 0) return "sunday";
  if (wd === 6) return "saturday";
  return "workday";
}

/** BDEW H0 dynamisation polynomial (t = day of year). */
export function h0Dynamisation(dayOfYear: number): number {
  const t = dayOfYear;
  return (
    -3.916649e-10 * t ** 4 +
    3.2e-7 * t ** 3 -
    7.02e-5 * t ** 2 +
    0.0021 * t +
    1.24
  );
}

export interface LoadOverlays {
  /** Annual EV charging energy at home, kWh (charged mostly evening/night). */
  evAnnualKwh?: number;
  /** Annual heat-pump electricity, kWh (weighted into the heating season). */
  heatPumpAnnualKwh?: number;
  /**
   * Share (0..1) of EV energy an EMS shifts into the PV window (surplus
   * charging, 9–16 h). Requires a surplus-capable wallbox + energy manager.
   */
  evPvSurplusShare?: number;
  /** SG-Ready preheating: shifts ~20 % of HP energy into the midday window. */
  hpSgReadyOptimized?: boolean;
}

const EV_SHAPE = n([6, 5, 4, 3, 2, 2, 2, 2, 1, 1, 1, 1, 1, 1, 2, 2, 4, 8, 12, 14, 12, 8, 5, 5]);
/** PV-surplus window shape (EMS-controlled charging / SG-Ready preheating). */
const PV_WINDOW_SHAPE = n([0, 0, 0, 0, 0, 0, 0, 0, 0, 2, 5, 8, 10, 10, 8, 5, 2, 0, 0, 0, 0, 0, 0, 0]);
const HP_MONTH_WEIGHT = n([16, 14, 12, 8, 4, 2, 2, 2, 4, 8, 12, 16]);
const HP_SHAPE = n([5, 5, 5, 5, 6, 7, 7, 6, 5, 4, 4, 4, 4, 4, 4, 4, 5, 5, 5, 5, 4, 4, 4, 4]);

/**
 * Build an hourly consumption series (kW) for a full simulation period,
 * aligned to the given hourly timestamps.
 */
export function buildLoadSeries(
  timesMs: number[],
  profile: ProfileId,
  annualConsumptionKwh: number,
  overlays: LoadOverlays = {},
): number[] {
  if (timesMs.length === 0) return [];
  const years = new Set(timesMs.map((t) => new Date(t).getUTCFullYear())).size;

  // First pass: raw weights
  const weights = new Array<number>(timesMs.length);
  let weightSum = 0;
  for (let i = 0; i < timesMs.length; i++) {
    const d = new Date(timesMs[i]!);
    const shape = SHAPES[profile][seasonOf(d)][dayTypeOf(d)];
    let w = shape[d.getUTCHours()]!;
    if (profile === "H0") {
      const start = Date.UTC(d.getUTCFullYear(), 0, 0);
      const doy = Math.floor((timesMs[i]! - start) / 86400000);
      w *= h0Dynamisation(doy);
    }
    weights[i] = w;
    weightSum += w;
  }

  const base = annualConsumptionKwh * years;
  const out = weights.map((w) => (base * w!) / weightSum);

  // EV overlay — optionally split between the default evening/night shape and
  // an EMS-controlled PV-surplus window (9–16 h).
  if (overlays.evAnnualKwh && overlays.evAnnualKwh > 0) {
    const surplusShare = Math.max(0, Math.min(1, overlays.evPvSurplusShare ?? 0));
    const evTotal = overlays.evAnnualKwh * years;
    let evSum = 0;
    let midSum = 0;
    const evW = new Array<number>(timesMs.length);
    const midW = new Array<number>(timesMs.length);
    for (let i = 0; i < timesMs.length; i++) {
      const h = new Date(timesMs[i]!).getUTCHours();
      evW[i] = EV_SHAPE[h]!;
      midW[i] = PV_WINDOW_SHAPE[h]!;
      evSum += evW[i]!;
      midSum += midW[i]!;
    }
    for (let i = 0; i < out.length; i++) {
      out[i]! += (evTotal * (1 - surplusShare) * evW[i]!) / evSum;
      if (midSum > 0) out[i]! += (evTotal * surplusShare * midW[i]!) / midSum;
    }
  }

  // Heat-pump overlay — SG-Ready systems preheat in the PV window (thermal
  // storage), shifting ~20 % of electrical demand into midday.
  if (overlays.heatPumpAnnualKwh && overlays.heatPumpAnnualKwh > 0) {
    const sgShift = overlays.hpSgReadyOptimized ? 0.2 : 0;
    const hpTotal = overlays.heatPumpAnnualKwh * years;
    let hpSum = 0;
    let midSum = 0;
    const hpW = new Array<number>(timesMs.length);
    const midW = new Array<number>(timesMs.length);
    for (let i = 0; i < timesMs.length; i++) {
      const d = new Date(timesMs[i]!);
      hpW[i] = HP_MONTH_WEIGHT[d.getUTCMonth()]! * HP_SHAPE[d.getUTCHours()]!;
      midW[i] = HP_MONTH_WEIGHT[d.getUTCMonth()]! * PV_WINDOW_SHAPE[d.getUTCHours()]!;
      hpSum += hpW[i]!;
      midSum += midW[i]!;
    }
    for (let i = 0; i < out.length; i++) {
      out[i]! += (hpTotal * (1 - sgShift) * hpW[i]!) / hpSum;
      if (midSum > 0) out[i]! += (hpTotal * sgShift * midW[i]!) / midSum;
    }
  }

  return out;
}
